import React, { useState } from 'react';
import { message, Spin } from 'antd';
import { ArchiveFilledIcon, ArchiveIcon, ArrowRightIcon, SecondNoteIcon, TestEyeIcon, TestStartIcon } from 'icons';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { openAuthModal } from 'modules/auth';
import { globalConst } from 'modules/global';
import { openModal } from 'modules/learning-centers';
import { ITestProps, TestModal } from 'modules/mavzuli-test';
import { useSaveTest } from 'modules/saved-tests';

import { Visible } from 'components';

const TestCard = ({
  isNew,
  testId,
  isSaved,
  testUrl,
  teacher,
  viewCount,
  testCount,
  lessonName,
  testRating,
  subjectName,
  learningCenter
}: ITestProps) => {
  const t = useTranslations();
  const { push } = useRouter();
  const saveTest = useSaveTest();
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState<boolean>(false);
  const [testInfo, setTestInfo] = useState<ITestProps>();
  const [isSavedState, setIsSavedState] = useState<boolean>(isSaved);

  const user = useAppSelector(state => state.user);

  const handleSaveTest = (newStatus: boolean) => {
    if (testId == null) return;

    if (user.phone) {
      saveTest.mutate(
        { id: testId, status: newStatus },
        {
          onSuccess: () => {
            setIsSavedState(newStatus);

            message.success(t('Muvaffaqiyatli_bajarildi'));
          }
        }
      );
    } else {
      dispatch(openAuthModal())
    }
  };

  const onOpenLearningCenter = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    dispatch(
      openModal({
        learning_center: learningCenter as never,
        learning_center_id: learningCenter?.id || 0
      })
    );
  };

  return (
    <div>
      <div
        onClick={() => {
          setOpen(true);

          setTestInfo({
            subjectName,
            lessonName,
            testCount,
            testRating,
            viewCount,
            isSaved,
            testId,
            testUrl,
            learningCenter,
            teacher,
            isNew
          });
        }}
        className="flex h-full cursor-pointer flex-col space-y-4 rounded-2xl bg-white p-4 shadow-md"
      >
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="rounded-md bg-gray-100 px-2 py-1 text-xs">{subjectName}</div>
              <Visible if={isNew}>
                <div className="rounded-md bg-orange-500 px-2 py-1 text-xs text-white">{t('Yangi')}</div>
              </Visible>
            </div>
            <div
              onClick={e => {
                e.stopPropagation();
                handleSaveTest(!isSavedState);
              }}
              className="cursor-pointer"
            >
              <Visible
                if={saveTest.isLoading}
                else={
                  <Visible if={isSavedState} else={<ArchiveIcon />}>
                    <ArchiveFilledIcon />
                  </Visible>
                }
              >
                <Spin size="small" />
              </Visible>
            </div>
          </div>
          <p className="mt-2 line-clamp-2 text-xs font-medium">{lessonName}</p>
        </div>

        <Visible if={teacher}>
          <Link
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();

              push(`${globalConst.pageNames.TEACHER}/${teacher?.slug || ''}`);
            }}
            href={`${globalConst.pageNames.TEACHER}/${teacher?.slug || ''}`}
            className="flex items-center justify-between rounded-lg bg-gray-100 px-1.5 py-1"
          >
            <div className="flex items-center gap-2">
              {teacher?.image ? (
                <div className="relative h-6 w-6">
                  <Image
                    fill
                    src={teacher?.image?.small}
                    alt={teacher?.full_name || ''}
                    className="rounded-full !object-cover"
                  />
                </div>
              ) : (
                <div
                  title={teacher?.full_name}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-600 font-medium text-white"
                >
                  {teacher?.full_name?.at(0) || '-'}
                </div>
              )}
              <div className="flex flex-col">
                <p className="text-[0.625rem] font-medium text-gray-800">{teacher?.full_name}</p>
                <span className="text-[0.5rem] font-medium text-gray-400">{t("O'qituvchi")}</span>
              </div>
            </div>
            <ArrowRightIcon />
          </Link>
        </Visible>

        <Visible if={learningCenter}>
          <div
            onClick={onOpenLearningCenter}
            className="flex items-center justify-between rounded-lg bg-gray-100 px-1.5 py-1"
          >
            <div className="flex items-center gap-2">
              {learningCenter?.logo ? (
                <div className="relative h-6 w-6">
                  <Image
                    fill
                    src={learningCenter?.logo}
                    alt={learningCenter?.name || ''}
                    className="rounded-full !object-cover"
                  />
                </div>
              ) : (
                <div
                  title={learningCenter?.name}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-600 font-medium text-white"
                >
                  {learningCenter?.name?.at(0) || '-'}
                </div>
              )}
              <div className="flex flex-col">
                <p className="text-[0.625rem] font-medium text-gray-800">{learningCenter?.name}</p>
                <span className="text-[0.5rem] font-medium text-gray-400">{t("O'quv markazi")}</span>
              </div>
            </div>
            <ArrowRightIcon />
          </div>
        </Visible>

        <div className="flex items-end justify-between text-xs">
          <div className="flex items-center gap-1">
            <TestStartIcon />
            {testRating}
          </div>
          <div className="flex items-center gap-1">
            <SecondNoteIcon />
            {testCount} {t('test')}
          </div>
          <div className="flex items-center gap-1">
            <TestEyeIcon />
            {viewCount}
          </div>
        </div>
      </div>
      <TestModal open={open} onClose={() => setOpen(false)} selectedData={testInfo} />
    </div>
  );
};

export default TestCard;
